import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import { Wallet, Trash2, Calendar, Filter } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { logError } from "../lib/logger";

function fmtBRL(v) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(v) || 0);
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function defaultRange(kind) {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (kind === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: fmt(d), to: fmt(now) };
  }
  if (kind === "year") return { from: `${now.getFullYear()}-01-01`, to: fmt(now) };
  if (kind === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmt(start), to: fmt(end) };
  }
  return { from: "", to: "" };
}

export default function PaymentsHistory() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(defaultRange("month"));
  const [userFilter, setUserFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set("from", range.from);
      if (range.to) params.set("to", range.to);
      const qs = params.toString();
      const { data } = await api.get(`/payments${qs ? `?${qs}` : ""}`);
      setItems(data);
    } catch (e) {
      logError(e);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const usersList = useMemo(() => {
    const ids = new Map();
    items.forEach((i) => {
      if (i.user_id && !ids.has(i.user_id))
        ids.set(i.user_id, i.user_name || i.user_id);
    });
    return Array.from(ids.entries());
  }, [items]);

  const filtered = useMemo(() => {
    if (userFilter === "all") return items;
    return items.filter((i) => i.user_id === userFilter);
  }, [items, userFilter]);

  const totalPeriod = filtered.reduce((s, i) => s + Number(i.amount || 0), 0);

  async function remove(p) {
    if (!(await confirm({
      title: "Excluir pagamento?",
      description: `Pagamento de ${fmtBRL(p.amount)} para ${p.user_name || "colaborador"} será removido do histórico.`,
      destructive: true,
      confirmText: "Excluir",
    }))) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Pagamento removido");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
          Financeiro
        </div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wallet /> Histórico de pagamentos
        </h1>
        <div className="text-sm text-slate-500">
          Pagamentos efetuados aos colaboradores.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="cm-card p-4">
          <div className="text-xs text-slate-500 uppercase">Total pago no período</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{fmtBRL(totalPeriod)}</div>
        </div>
        <div className="cm-card p-4">
          <div className="text-xs text-slate-500 uppercase">Pagamentos registrados</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{filtered.length}</div>
        </div>
        <div className="cm-card p-4">
          <div className="text-xs text-slate-500 uppercase">Colaboradores únicos</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {new Set(filtered.map((i) => i.user_id)).size}
          </div>
        </div>
      </div>

      <div className="cm-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500 font-semibold mr-1">Período:</span>
        {[
          { k: "all", label: "Todos" },
          { k: "month", label: "Este mês" },
          { k: "last_month", label: "Mês passado" },
          { k: "year", label: "Este ano" },
        ].map((opt) => (
          <button
            key={opt.k}
            data-testid={`payments-range-${opt.k}`}
            onClick={() => setRange(defaultRange(opt.k))}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            {opt.label}
          </button>
        ))}
        <DatePickerUS
          value={range.from}
          onChange={(v) => setRange({ ...range, from: v })}
          testId="payments-range-from"
        />
        <span className="text-slate-400">→</span>
        <DatePickerUS
          value={range.to}
          onChange={(v) => setRange({ ...range, to: v })}
          testId="payments-range-to"
        />
      </div>

      {usersList.length > 1 && (
        <div className="cm-card p-3 mb-4 flex flex-wrap items-center gap-2">
          <Filter size={14} className="text-slate-400" />
          <span className="text-xs text-slate-500 font-semibold mr-1">Colaborador:</span>
          <button
            data-testid="payments-user-all"
            onClick={() => setUserFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              userFilter === "all"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            Todos
          </button>
          {usersList.map(([uid, name]) => (
            <button
              key={uid}
              data-testid={`payments-user-${uid}`}
              onClick={() => setUserFilter(uid)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                userFilter === uid
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <div className="cm-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <Wallet size={40} className="mx-auto mb-3 text-slate-300" />
            Nenhum pagamento registrado nesse período.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Data do pagamento</th>
                <th className="text-left p-3">Colaborador</th>
                <th className="text-left p-3">Período de referência</th>
                <th className="text-right p-3">Dias / Casas</th>
                <th className="text-left p-3">Método</th>
                <th className="text-right p-3">Valor</th>
                <th className="text-left p-3">Registrado por</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`payment-row-${p.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                    {fmtDateTime(p.paid_at)}
                  </td>
                  <td className="p-3 font-medium">
                    {p.user_name || p.user_id?.slice(0, 8)}
                    {p.zelle_venmo && (
                      <div className="text-xs text-slate-500">{p.zelle_venmo}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-600">
                    {p.period_label || (p.period_from && p.period_to ? `${p.period_from} → ${p.period_to}` : "—")}
                  </td>
                  <td className="p-3 text-right text-xs text-slate-600">
                    {p.days_worked || 0} / {p.houses || 0}
                  </td>
                  <td className="p-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[11px]">
                      {p.method || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-right font-bold text-emerald-700">
                    {fmtBRL(p.amount)}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{p.paid_by_name || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => remove(p)}
                      data-testid={`payment-delete-${p.id}`}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                      title="Excluir"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
